import { useEffect, useMemo, useState } from 'react'

import { useAccount, useChainId } from 'wagmi'

import { GasParams } from 'types/entities'

import { useBalances } from 'hooks/wallet'
import { useNativeCurrency } from 'hooks/token'
import { useGetBalancesRequest } from 'hooks/api'
import { useTopUp, useSelfGeneratedState, useAirdropState } from 'hooks/airdrop'

import { useGasParams } from 'state/airdrop/hooks'
import { useSelfGeneratedAddress, useTransactionsHistory, useUpdateTransactionAndWait } from 'state/user/hooks'

import { errors } from 'constants/errors'
import { convertStringObjToBigInt } from 'utils/helpers'
import { parseErrorToUserReadableMessage } from 'utils/errors'
import { TxStatuses, TxTypes, AirdropStatus } from 'constants/transaction'

import { Button, Loader } from 'components/ui'

import { useCallNotification } from 'notifications/hooks'
import { NotificationTypes } from 'notifications/service'

import { AirdropState } from './AirdropState'

import { SendingState, StepName, StepStatus } from './types'
import { Wrapper, Text, WrapContent, InfoText } from './Style'

type Props = {
  isLoading: boolean
  airdropId: string
  onCurrentClose?: VoidFunction
  sendingState: SendingState | null
  setSendingState: (sendingState: SendingState) => void
}

export function TopUp({ sendingState, airdropId, onCurrentClose, setSendingState }: Props) {
  const chainId = useChainId()
  const { address } = useAccount()
  const native = useNativeCurrency()

  const gasParams = useGasParams()

  const airdropState = useAirdropState()
  const selfGeneratedState = useSelfGeneratedState()
  const privateKeyAddress = useSelfGeneratedAddress()
  const updateTransactionAndWait = useUpdateTransactionAndWait()

  const balances = useBalances(privateKeyAddress)
  const transactionsHistory = useTransactionsHistory()

  const topUp = useTopUp()
  const callNotification = useCallNotification()
  const balancesRequest = useGetBalancesRequest()

  const [error, setError] = useState('')
  const [txHash, setTxHash] = useState('')

  const topUpTransaction = useMemo(
    () => Object.values(transactionsHistory).find((tx) => tx.txHash === txHash),
    [transactionsHistory, txHash],
  )

  const topUpTxStatus = useMemo(() => {
    if (!topUpTransaction) {
      return AirdropStatus.NOT_STARTED
    }
    if (topUpTransaction?.status === TxStatuses.SUCCESS) {
      return AirdropStatus.SUCCESS
    }

    if (topUpTransaction?.status === TxStatuses.FAIL) {
      return AirdropStatus.FAILED
    }
    return AirdropStatus.PENDING
  }, [topUpTransaction])

  useEffect(() => {
    if (selfGeneratedState.shouldTopUpPk) {
      onTopUp()
    }
  }, [])

  const clearLocalState = () => {
    setError('')
  }

  const onTopUp = async () => {
    try {
      clearLocalState()

      setSendingState({ name: StepName.TOP_UP, status: StepStatus.PENDING })

      if (!privateKeyAddress) {
        throw new Error(errors.validation.PK_IS_REQUIRED)
      }

      if (!address) {
        throw new Error(errors.wallet.WALLET_REQUIRED)
      }

      if (!gasParams) {
        throw new Error(errors.validation.GAS_PARAMS_ARE_REQUIRED)
      }

      const topUpAmount = airdropState.approximateCost - BigInt(balances.nativeBalance)

      const bigintGasParams = convertStringObjToBigInt(gasParams) as GasParams

      const txHash = await topUp(privateKeyAddress, topUpAmount, bigintGasParams)
      setTxHash(txHash)

      await updateTransactionAndWait({
        txHash,
        chainId,
        airdropId,
        token: native,
        from: address,
        to: privateKeyAddress,
        type: TxTypes.TOP_UP_SELF_GENERATED,
        value: airdropState.approximateCost.toString(),
      })

      if (txHash) {
        callNotification({ type: NotificationTypes.SUCCESS, message: 'Top up transaction successfully sent' })
      }
      setSendingState({ name: StepName.TOP_UP, status: StepStatus.SUCCESS })
      balancesRequest.refetch()
    } catch (err) {
      setError(parseErrorToUserReadableMessage(err))
      setSendingState({ name: StepName.TOP_UP, status: StepStatus.FAILED })
      callNotification({ type: NotificationTypes.ERROR, message: err.message })
    }
  }

  const handleChangeStep = async () => {
    if (!gasParams) {
      return
    }
    const _gasParams = convertStringObjToBigInt(gasParams)

    const shouldTopUpPk = await selfGeneratedState.getShouldTopUpPk(_gasParams as GasParams)

    if (shouldTopUpPk) {
      await onTopUp()
    } else {
      setSendingState({ name: StepName.SEND, status: StepStatus.PENDING })
    }
  }

  const isDisabled = useMemo(
    () => topUpTxStatus !== AirdropStatus.SUCCESS || balancesRequest.isLoading,
    [topUpTxStatus, balancesRequest.isLoading],
  )
  const isLoading = useMemo(
    () => sendingState?.status === StepStatus.PENDING || topUpTxStatus === AirdropStatus.PENDING,
    [sendingState?.status, topUpTxStatus],
  )

  return (
    <>
      <InfoText>Top up private key wallet to make an airdrop sending</InfoText>

      {balancesRequest.isLoading && (
        <WrapContent>
          <Wrapper>
            <Loader size={7.2} />
          </Wrapper>
          <Text>Fetching balance</Text>
        </WrapContent>
      )}

      <AirdropState
        error={error}
        txHash={txHash}
        txStatus={topUpTxStatus}
        onCurrentClose={onCurrentClose}
        txType={TxTypes.TOP_UP_SELF_GENERATED}
      />

      {error ? (
        <Button fullSize size='large' onClick={onTopUp}>
          _retry
        </Button>
      ) : (
        <Button fullSize size='large' disabled={isDisabled} isLoading={isLoading} onClick={handleChangeStep}>
          _continue
        </Button>
      )}
    </>
  )
}
